import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area
} from 'recharts';
import {
  Users, TrendingUp, Activity, Calendar, Search, LogOut,
  Eye, BarChart3, User, Mail, Clock, Trophy, Target
} from 'lucide-react';
import moment from 'moment-timezone';
import '../index.css';

const API = process.env.REACT_APP_API_BASE_URL || '';

export default function Dashboard() {
  // --- Auth & UI State ---
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authErr, setAuthErr] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // --- Data State ---
  const [users, setUsers] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [userMeals, setUserMeals] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [timeRange, setTimeRange] = useState('all');
  const [chartType, setChartType] = useState('bar');

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [expandedMeals, setExpandedMeals] = useState({});

  const toggleMeal = (id) => {
    setExpandedMeals(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // --- Auth Helpers ---
  const fetchWithAuth = async (path) => {
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.status === 401) {
      handleLogout();
      throw new Error('Session expired, please login again');
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Network error');
    }
    return res.json();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthErr('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Login failed');
      localStorage.setItem('token', body.token);
      setToken(body.token);
    } catch (err) {
      setAuthErr(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUsers([]);
    setSummaries([]);
    setUserMeals([]);
    setSelectedUser(null);
    setShowModal(false);
  };

  // --- Load Users & Summaries ---
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError('');
    Promise.all([
      fetchWithAuth('/api/admin/users'),
      fetchWithAuth('/api/admin/meals-summary')
    ])
      .then(([u, s]) => {
        setUsers(Array.isArray(u) ? u : []);
        setSummaries(Array.isArray(s) ? s : []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const selectUser = async (id) => {
    setLoading(true);
    setError('');
    try {
      const meals = await fetchWithAuth(`/api/admin/meals-by-user/${id}`);
      setUserMeals(Array.isArray(meals) ? meals : []);
      setSelectedUser(id);
      setShowModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Filtering & Metrics ---
  const filteredUsers = useMemo(
    () => users.filter(u =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
    ),
    [users, search]
  );

  const totalUsers = users.length;
  const totalMeals = summaries.reduce((sum, x) => sum + (x.mealCount || 0), 0);
  const avgMealsOverall = totalUsers ? (totalMeals / totalUsers).toFixed(1) : 0;
  
  // Calculate users with meals this week
  const usersWithMealsThisWeek = useMemo(() => {
    const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const usersWithRecentMeals = new Set();
    summaries.forEach(s => {
      if (new Date(s.date).getTime() >= oneWeekAgo) {
        usersWithRecentMeals.add(s.userName); // Assuming userName is unique enough or map to userId
      }
    });
    return users.filter(u => usersWithRecentMeals.has(u.name)).length;
  }, [users, summaries]);

  // --- Time-Filtered Summaries ---
  const filteredSummaries = useMemo(() => {
    if (timeRange === 'all') return summaries;
    const now = Date.now();
    return summaries.filter(s => {
      const d = new Date(s.date).getTime();
      if (timeRange === 'today') {
        const today = new Date().toDateString();
        return new Date(s.date).toDateString() === today;
      }
      if (timeRange === 'week') return now - d <= 7 * 24 * 60 * 60 * 1000;
      if (timeRange === 'month') return now - d <= 30 * 24 * 60 * 60 * 1000;
      return true;
    });
  }, [summaries, timeRange]);

  // --- Chart Data ---
  const dailyEntries = useMemo(() => {
    const map = {};
    filteredSummaries.forEach(s => {
      if (s.date && s.mealCount != null) {
        const label = moment(s.date, "YYYY-MM-DD").format("MMM D"); // Use localDate as s.date
        map[label] = (map[label] || 0) + s.mealCount;
      }
    });
    return Object.entries(map)
      .map(([date, mealCount]) => ({ date, mealCount }))
      .sort((a, b) => moment(a.date, "MMM D").toDate() - moment(b.date, "MMM D").toDate())
      .slice(-14);
  }, [filteredSummaries]);

  const mealsByUser = useMemo(() => {
    const map = {};
    filteredSummaries.forEach(s => {
      if (s.userName) map[s.userName] = (map[s.userName] || 0) + s.mealCount;
    });
    return Object.entries(map)
      .map(([userName, mealCount]) => ({ userName, mealCount }))
      .sort((a, b) => b.mealCount - a.mealCount)
      .slice(0, 10);
  }, [filteredSummaries]);

  // --- User Meal Modal Data Calculations ---
  const mealStats = useMemo(() => {
    const totalDaysWithMeals = new Set(userMeals.map(m => new Date(m.date).toDateString())).size;
    const totalCalories = userMeals.reduce((s, m) => s + (m.totalCalories || 0), 0);
    const totalProtein = userMeals.reduce((s, m) => s + (m.totalProtein || 0), 0);
    const totalCarbs = userMeals.reduce((s, m) => s + (m.totalCarbs || 0), 0);
    const totalFats = userMeals.reduce((s, m) => s + (m.totalFats || 0), 0);

    return {
      avgCalories: totalDaysWithMeals ? (totalCalories / totalDaysWithMeals).toFixed(0) : 0,
      avgProtein: totalDaysWithMeals ? (totalProtein / totalDaysWithMeals).toFixed(0) : 0,
      avgCarbs: totalDaysWithMeals ? (totalCarbs / totalDaysWithMeals).toFixed(0) : 0,
      avgFats: totalDaysWithMeals ? (totalFats / totalDaysWithMeals).toFixed(0) : 0,
    };
  }, [userMeals]);


  const mealsByDate = useMemo(() => {
    const grp = {};
    userMeals.forEach(m => {
      const dateObj = moment(m.date).tz(userTimezone);
      const dayOfWeek = dateObj.format('ddd');
      const date = dateObj.format('M/D/YYYY');

      const label = `${dayOfWeek}, ${date}`;
      grp[label] = grp[label] || [];
      grp[label].push(m);
    });
    return Object.entries(grp)
      .sort((a, b) => new Date(b[0].split(', ')[1]) - new Date(a[0].split(', ')[1])); // Sort by actual date
  }, [userMeals]);

  // --- Styles ---
  const styles = {
    gradientBg: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh' },
    loginCard: { background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '15px', boxShadow: '0 15px 30px rgba(0,0,0,0.1)' },
    dashboardBg: { background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' },
    card: { border: 'none', borderRadius: '12px', boxShadow: '0 3px 15px rgba(0,0,0,0.06)', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' },
    statCard: { border: 'none', borderRadius: '15px', boxShadow: '0 6px 24px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', cursor: 'pointer' },
    modalOverlay: { background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    modalContent: { border: 'none', borderRadius: '15px', boxShadow: '0 15px 45px rgba(0,0,0,0.2)' }
  };

  // --- Login Screen ---
  if (!token) {
    return (
      <div style={styles.gradientBg} className="d-flex align-items-center justify-content-center p-3">
        <div className="text-center" style={{ maxWidth: 380, width: '100%' }}>
          <div className="mb-4">
            <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 70, height: 70, background: 'rgba(255,255,255,0.2)', borderRadius: '50%', backdropFilter: 'blur(10px)' }}>
              <Activity size={35} className="text-white" />
            </div>
            <h1 className="text-white mb-2" style={{ fontWeight: 300, fontSize: '2.2rem' }}>Metabolify</h1>
            <p className="text-white opacity-75 small">Admin Dashboard</p>
          </div>
          <div style={styles.loginCard} className="p-4">
            <form onSubmit={handleLogin}>
              <h5 className="mb-4 text-center" style={{ fontWeight: 300 }}>Welcome Back</h5>
              {authErr && <div className="alert alert-danger border-0 rounded-3 mb-3 small" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>{authErr}</div>}
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text border-0 bg-light"><Mail size={16} className="text-muted" /></span>
                  <input type="email" className="form-control border-0 bg-light form-control-sm" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={{ borderRadius: '0 8px 8px 0' }} />
                </div>
              </div>
              <div className="mb-4">
                <div className="input-group">
                  <span className="input-group-text border-0 bg-light"><Eye size={16} className="text-muted" /></span>
                  <input type="password" className="form-control border-0 bg-light form-control-sm" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={{ borderRadius: '0 8px 8px 0' }} />
                </div>
              </div>
              <button type="submit" className="btn w-100 py-2 border-0 rounded-3" style={{ background: 'linear-gradient(45deg, #667eea, #764ba2)', color: '#fff', fontWeight: 500 }} disabled={authLoading}>
                {authLoading ? <> <span className="spinner-border spinner-border-sm me-2" /> Signing in... </> : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // --- Loading & Error States ---
  if (loading) {
    return (
      <div style={styles.dashboardBg} className="d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: 40, height: 40 }} />
          <p className="text-muted small">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={styles.dashboardBg} className="d-flex flex-column align-items-center justify-content-center p-3">
        <div className="alert alert-danger border-0 rounded-3 mb-3 text-center" style={styles.card}>
          <h6 className="alert-heading fw-bold">Oops! Something went wrong</h6>
          <p className="mb-0 small">{error}</p>
        </div>
        <button className="btn btn-primary btn-sm px-4 py-2 rounded-3" onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // --- Main Dashboard UI ---
  return (
    <div style={styles.dashboardBg} className="min-vh-100">
      <div className="container-fluid p-3"> {/* Slightly smaller padding */}
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3"> {/* Smaller margin */}
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center justify-content-center me-3" style={{ width: 45, height: 45, background: 'linear-gradient(45deg, #667eea, #764ba2)', borderRadius: 10 }}>
              <Activity size={22} className="text-white" />
            </div>
            <div>
              <h4 className="mb-0" style={{ fontWeight: 600 }}>Metabolify Admin</h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Manage your nutrition platform</p>
            </div>
          </div>
          <button className="btn btn-outline-dark btn-sm px-3 py-2 rounded-3 d-flex align-items-center" onClick={handleLogout}>
            <LogOut size={16} className="me-2" /> Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div className="row g-3 mb-3"> {/* Smaller gutter and margin */}
          {[
            { title: 'Total Users', value: totalUsers, icon: <Users size={30} className="opacity-50" />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
            { title: 'Total Meals', value: totalMeals, icon: <Target size={30} className="opacity-50" />, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
            { title: 'Avg Meals/User', value: avgMealsOverall, icon: <TrendingUp size={30} className="opacity-50" />, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
            { title: 'Users w/ Meals This Week', value: usersWithMealsThisWeek, icon: <Calendar size={30} className="opacity-50" />, gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }
          ].map((stat, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-3">
              <div className="card h-100 border-0 position-relative overflow-hidden" style={{ ...styles.statCard, background: stat.gradient }}>
                <div className="card-body text-white p-3"> {/* Smaller padding */}
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="card-title opacity-75 mb-1" style={{ fontSize: '0.9rem' }}>{stat.title}</h6> {/* Smaller font */}
                      <h3 className="mb-0" style={{ fontWeight: 700 }}>{stat.value}</h3> {/* Smaller font */}
                    </div>
                    {stat.icon}
                  </div>
                </div>
                <div className="position-absolute bottom-0 end-0" style={{ width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', transform: 'translate(20px, 20px)' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Users Table */}
        <div className="card mb-3" style={styles.card}> {/* Smaller margin */}
          <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center py-2"> {/* Smaller padding */}
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3" style={{ fontWeight: 600 }}>Users</h5>
              <span className="badge bg-primary rounded-pill">{filteredUsers.length}</span>
            </div>
            <div className="input-group" style={{ width: 250 }}> {/* Slightly narrower */}
              <span className="input-group-text border-0 bg-light"><Search size={16} className="text-muted" /></span>
              <input type="text" className="form-control border-0 bg-light form-control-sm" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0 small"> {/* Smaller font for table */}
              <thead className="table-light">
                <tr>
                  <th className="border-0 py-2" style={{ fontWeight: 600 }}><User size={14} className="me-2 text-muted" />Name</th>
                  <th className="border-0 py-2" style={{ fontWeight: 600 }}><Mail size={14} className="me-2 text-muted" />Email</th>
                  <th className="border-0 py-2" style={{ fontWeight: 600 }}><Calendar size={14} className="me-2 text-muted" />Registered</th>
                  <th className="border-0 py-2" style={{ fontWeight: 600 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u._id}>
                    <td className="py-2"> {/* Smaller padding */}
                      <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 35, height: 35 }}> {/* Smaller avatar */}
                          <span className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>{u.name.charAt(0).toUpperCase()}</span>
                        </div>
                        <span style={{ fontWeight: 500 }}>{u.name}</span>
                      </div>
                    </td>
                    <td className="py-2 text-muted">{u.email}</td>
                    <td className="py-2 text-muted">{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td className="py-2">
                      <button className="btn btn-sm btn-primary rounded-3 px-3 py-1" onClick={() => selectUser(u._id)}> {/* Smaller button */}
                        <Eye size={14} className="me-1" /> View Meals
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Filters & Chart Controls */}
        <div className="d-flex justify-content-between align-items-center mb-3"> {/* Smaller margin */}
          <div className="btn-group btn-group-sm"> {/* Smaller buttons */}
            {['all', 'today', 'week', 'month'].map(r => (
              <button key={r} onClick={() => setTimeRange(r)} className={`btn ${timeRange===r?'btn-primary':'btn-outline-primary'} rounded-3`} style={{ marginRight: r!=='month'?6:0 }}>
                {r.charAt(0).toUpperCase()+r.slice(1)}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2">
            {[['bar', <BarChart3 size={14}/>], ['line', <TrendingUp size={14}/>], ['area', <Activity size={14}/>]].map(([t, icon]) => (
              <button key={t} className={`btn btn-sm ${chartType===t?'btn-primary':'btn-outline-primary'} rounded-3`} onClick={() => setChartType(t)}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Charts Row */}
        <div className="row g-3 mb-3"> {/* Smaller gutter and margin */}
          <div className="col-12"> {/* Only one column for charts */}
            <div className="card h-100" style={styles.card}>
              <div className="card-header bg-transparent border-0 py-2"> {/* Smaller padding */}
                <h6 className="mb-0" style={{ fontWeight: 600 }}>Meal Entries Over Time</h6>
              </div>
              <div className="card-body p-0" style={{ height: 350 }}> {/* Smaller height */}
                <ResponsiveContainer>
                  {chartType === 'bar' && (
                    <BarChart data={dailyEntries} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}> {/* Smaller margins */}
                      <defs>
                        <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#764ba2" stopOpacity={0.8}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} /> {/* Smaller font */}
                      <YAxis tick={{ fontSize: 10 }} /> {/* Smaller font */}
                      <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '8px', boxShadow: '0 3px 15px rgba(0,0,0,0.1)' }}/>
                      <Bar dataKey="mealCount" fill="url(#barGradient)" radius={[3,3,0,0]} /> {/* Smaller radius */}
                    </BarChart>
                  )}
                  {chartType === 'line' && (
                    <LineChart data={dailyEntries} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '8px', boxShadow: '0 3px 15px rgba(0,0,0,0.1)' }}/>
                      <Line type="monotone" dataKey="mealCount" stroke="#667eea" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}/> {/* Thinner line, smaller dots */}
                    </LineChart>
                  )}
                  {chartType === 'area' && (
                    <AreaChart data={dailyEntries} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#764ba2" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '8px', boxShadow: '0 3px 15px rgba(0,0,0,0.1)' }}/>
                      <Area type="monotone" dataKey="mealCount" stroke="#667eea" fill="url(#areaGradient)" fillOpacity={0.6}/>
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Top Active Users */}
        <div className="card" style={styles.card}>
          <div className="card-header bg-transparent border-0 py-2 d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ fontWeight: 600 }}>Top Active Users</h6>
            <Trophy size={18} className="text-warning" />
          </div>
          <div className="card-body p-0" style={{ height: 280 }}> {/* Smaller height */}
            <ResponsiveContainer>
              <BarChart data={mealsByUser} layout="vertical" margin={{ top: 10, right: 10, left: 60, bottom: 10 }}> {/* Smaller margins */}
                <defs>
                  <linearGradient id="topUsersGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#f5576c" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="userName" type="category" width={80} tick={{ fontSize: 10 }} /> {/* Narrower Y-axis */}
                <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '8px', boxShadow: '0 3px 15px rgba(0,0,0,0.1)' }}/>
                <Bar dataKey="mealCount" fill="url(#topUsersGradient)" radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Meal Details Modal */}
        {showModal && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={styles.modalOverlay}
            onClick={(e) => {
              if (e.target.classList.contains('modal')) {
                setShowModal(false);
              }
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content" style={styles.modalContent}>
                <div className="modal-header border-0 pb-0 d-flex align-items-center">
                  <div className="d-flex align-items-center me-3">
                    <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 45, height: 45 }}>
                      <User size={20} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0" style={{ fontWeight: 600 }}>
                      {users.find(u => u._id === selectedUser)?.name}'s Meals
                    </h5>
                    <p className="text-muted small mb-0">{userMeals.length} total meals recorded</p>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                </div>

                <div className="modal-body pt-0 p-0" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {/* Sticky wrapper for averages + header */}
                  <div className="bg-white sticky-top" style={{ top: 0, zIndex: 20 }}>
                    {/* Daily Averages */}
                    <div className="row g-2 px-3 pt-3 pb-2 border-bottom" style={{ margin: 0 }}>
                      {[
                        { label: 'Avg. Calories/Day', value: mealStats.avgCalories, color: 'rgba(102,126,234,0.1)', textColor: 'text-primary' },
                        { label: 'Avg. Protein/Day', value: mealStats.avgProtein + 'g', color: 'rgba(245,87,108,0.1)', textColor: 'text-danger' },
                        { label: 'Avg. Carbs/Day', value: mealStats.avgCarbs + 'g', color: 'rgba(16,185,129,0.1)', textColor: 'text-success' },
                        { label: 'Avg. Fats/Day', value: mealStats.avgFats + 'g', color: 'rgba(245,158,11,0.1)', textColor: 'text-warning' }
                      ].map((stat, i) => (
                        <div key={i} className="col-6 col-md-3">
                          <div className="text-center p-2 rounded-3" style={{ background: stat.color }}>
                            <div className={`fw-bold ${stat.textColor}`} style={{ fontSize: '1.2rem' }}>
                              {stat.value}
                            </div>
                            <div className="small text-muted">{stat.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                    <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                      <table className="table table-hover mb-0 small">
                        <thead className="table-light">
                          <tr>
                            {['Date&Time', 'Meal', 'Calories', 'Protein', 'Carbs', 'Fats'].map((label, i) => (
                              <th
                                key={i}
                                className="border-0 py-2"
                                style={{
                                  fontWeight: 600,
                                  position: 'sticky',
                                  top: 0,
                                  background: '#f8f9fa',
                                  zIndex: 10
                                }}
                              >
                                {i === 0 ? <><Clock size={16} className="me-2 text-muted" />{label}</> : label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mealsByDate.map(([date, meals]) => (
                            <React.Fragment key={date}>
                              <tr className="table-secondary">
                                <td colSpan="6" className="py-2">
                                  <div className="d-flex align-items-center">
                                    <Calendar size={14} className="me-2 text-muted" />
                                    <span className="fw-bold">{date}</span>
                                    <span className="badge bg-primary ms-2">{meals.length} meals</span>
                                  </div>
                                </td>
                              </tr>
                              {meals.map(m => (
                                <React.Fragment key={m._id}>
                                  <tr onClick={() => toggleMeal(m._id)} style={{ cursor: 'pointer' }}>
                                    <td className="py-2">
                                      <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        {moment(m.date).tz(userTimezone).format('hh:mm A')}
                                      </span>
                                    </td>
                                    <td className="py-2">
                                      <div className="d-flex align-items-center">
                                        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 25, height: 25 }}>🍽️</div>
                                        <span style={{ fontWeight: 500 }}>{m.mealName}
                                          <span className="ms-2 text-muted">{expandedMeals[m._id] ? '▲' : '▼'}</span>
                                        </span>
                                      </div>
                                    </td>
                                    <td className="py-2"><span className="badge bg-primary rounded-pill">{m.totalCalories || 0}</span></td>
                                    <td className="py-2"><span className="badge bg-danger rounded-pill">{m.totalProtein || 0}g</span></td>
                                    <td className="py-2"><span className="badge bg-success rounded-pill">{m.totalCarbs || 0}g</span></td>
                                    <td className="py-2"><span className="badge bg-warning rounded-pill">{m.totalFats || 0}g</span></td>
                                  </tr>
                                  <tr>
                                    <td colSpan="6" className="py-0">
                                      <div
                                        className={`expandable-wrapper ${expandedMeals[m._id] ? 'show' : ''}`}
                                        style={{
                                          maxHeight: expandedMeals[m._id] ? '100px' : '0',
                                          overflow: 'hidden',
                                          transition: 'max-height 0.3s ease'
                                        }}
                                      >
                                        <div className="small text-muted px-3 py-2">
                                          <strong>Description:</strong> {m.description || 'No description'}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}